import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState
} from "react";
import useStyles from "./styles";
import { StoreSelect } from "../../../components/selects/StoreSelect";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  Icon,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import * as ProductApi from "../../../api/productApi";
import * as StoreInventoryApi from "../../../api/storeInventoryApi";
import * as StoreOrderApi from "../../../api/storeOrderApi";

import { StoreOrderSelect } from "./StoreOrderSelect";
import { Alert } from "@material-ui/lab";
import {
  Store,
  StoreOrder,
  StoreOrderItem,
  StoreOrderState
} from "../../../models/Store";
import { SpoilageReasonSelect } from "../../../components/selects/SpoilageReasonSelect";
import { OrderUnit } from "../../../models/Order";

import Fuse from "fuse.js";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../store";
import { Authenticated, hasRole, UserLocation } from "../../../models/User";
import { TenantProductEditor } from "../../tenants/Products/TenantProductEditor";
import { emptyProduct } from "../../../models/Product";
import { thunkFetchProductsV2 } from "../../../store/products/action";
import {
  Android,
  Clear,
  ClearAll,
  Link,
  TabletAndroid
} from "@material-ui/icons";
import Divider from "@material-ui/core/Divider";
import { StoresState } from "../../../store/stores/types";
import { UserContext } from "../../../UserProvider";
import { globalSettings } from "../../../models/Settings";
import { ConnectToPriceTag } from "../../tenants/Products/ConnectToPriceTag";
import { useI18n } from "../../../hooks/useI18n";
import { ScanditBarcodeScanner } from "../../../components/ScanditBarcodeScanner";

export const ProductDelivery2: FunctionComponent = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const theme = useTheme();
  const currentUser = useContext(UserContext) as Authenticated;
  const { t } = useI18n();

  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const userLocation = useSelector<AppState, UserLocation>(s => s.userLocation);
  const storesState = useSelector<AppState, StoresState>(s => s.stores);

  let { stores } = storesState;

  const [state, setState] = useState<any>({
    showScanner: false,
    showConnectToPriceTag: false,
    searchShowScanner: false,
    getItemFailed: false,
    deliverySuccess: false,
    deliveryFailed: false,
    registerFailed: false,
    steps: [1, 2, 3],
    selectedStoreId: userLocation.storeId,
    selectedStoreName: userLocation.storeName
  });

  useEffect(() => {
    if (stores.length === 1) {
      setState({
        ...state,
        steps: [0, 1, 2],
        selectedStoreId: stores[0].id,
        selectedStoreName: stores[0].name
      });
    }
  }, [stores]);

  const totalQuantityDelivered = () => {
    const {
      deliveryItem: { correctedOnShelf, quantityDelivered }
    } = state;
    return (isNaN(correctedOnShelf) ? 0 : correctedOnShelf) + quantityDelivered;
  };

  const calculateOrderItemQuantityAfterDeliver = (item: StoreOrderItem) => {
    let total = 0;

    if (item.unit === OrderUnit.Kfp) {
      total += item.quantity;
    } else {
      total += (item.distributionPackageSize ?? 1) * item.quantity;
    }
    return (item.currentlyOnShelf ?? 0) + (isNaN(total) ? 0 : total);
  };

  const disabled = (item: StoreOrderItem) => {
    if (item.quantity === undefined) {
      return true;
    } else if (
      item.discrepancyQuantity &&
      item.discrepancyQuantity > 0 &&
      !item.discrepancyReasonId
    ) {
      return true;
    }
    return false;
  };

  const getOrderItems = () => {
    const {
      searchPhrase,
      orderToDeliver: { items }
    } = state;

    const searchOptions: any = {
      keys: ["searchMetaData"]
    };

    const all = items.map((item: StoreOrderItem) => ({
      ...item,
      searchMetaData: `${item.product.brandName}, ${item.product.brandName}, ${item.product.name}, ${item.product.articleNumber} ${item.product.gtin}`
    }));

    let fused: any =
      searchPhrase !== undefined && searchPhrase !== ""
        ? new Fuse(all, searchOptions).search(searchPhrase)
        : new Fuse(all, searchOptions);

    fused = fused.list ? fused.list : fused;

    return fused;
  };

  const loadProduct = async (gtin: string) => {
    const result = await StoreInventoryApi.getSingleByGTIN(
      state.selectedStoreId,
      gtin
    );

    if (result.type === "OK") {
      const { value } = result;
      const { product } = value;
      setState({
        ...state,
        manualInput: gtin,
        deliveryItem: {
          productId: product.id,
          orderInfo: product.orderInfo,
          gtin: product.gtin,
          productName: product.name,
          brandName: product.brandName,
          size: product.details?.size,
          onShelfBeforeDelivery: value.onShelf,
          correctedOnShelf: value.onShelf,
          quantityDelivered:
            product.orderInfo?.distributionPackageSize ?? undefined
        },
        scanning: false,
        delivering: true
      });
    } else {
      if (result.details.status === 404) {
        if (result.details.detail === "ProductNotRegistered") {
          setState({
            ...state,
            scanning: false,
            manualInput: gtin,
            registeringInSystem: true
          });
        } else {
          setState({
            ...state,
            scanning: false,
            manualInput: gtin,
            registeringInStore: true
          });
        }
      } else {
        setState({
          ...state,
          getItemFailed: true,
          error: result.details.detail
        });
      }
    }
  };

  return (
    <div>
      <div style={{ marginLeft: 10 }}>
        {state.steps[0] === 1 && (
          <>
            <h3>1. Select delivery destination</h3>
            <div>
              <StoreSelect
                className={classes.select}
                filterOpened={true}
                value={state.selectedStoreId ?? ""}
                onSelected={store => {
                  setState({
                    ...state,
                    selectedStoreId: store.id,
                    selectedStoreName: store.name,
                    deliverySource: hasRole(currentUser, ["DistributorManager"])
                      ? "Distributor"
                      : undefined
                  });
                }}
              />
            </div>
          </>
        )}
        {state && state?.selectedStoreId && (
          <>
            <h3>{state.steps[1]}. Select delivery source</h3>
            <div
              style={{
                display: "flex",
                marginTop: 20
              }}
            >
              <FormControl>
                <InputLabel id="delivery-source-select-label">
                  Select
                </InputLabel>
                <Select
                  labelId="delivery-select-source"
                  style={
                    state.deliverySource === "Distributor"
                      ? { width: 200, marginRight: 30 }
                      : { width: 300 }
                  }
                  value={state?.deliverySource ?? ""}
                  onChange={event => {
                    setState({
                      ...state,
                      deliverySource: event.target.value,
                      orderToDeliver: undefined
                    });
                  }}
                >
                  <MenuItem value="Distributor">
                    {t("common.formFields.distributor")}
                  </MenuItem>
                  {!hasRole(currentUser, ["DistributorManager"]) && (
                    <MenuItem value="Order">
                      {t("common.formFields.order")}
                    </MenuItem>
                  )}
                </Select>
              </FormControl>

              {state.deliverySource === "Distributor" && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={async () => {
                    setState({
                      ...state,
                      showScanner: true,
                      scanning: true
                    });
                  }}
                >
                  Scan
                </Button>
              )}
            </div>
          </>
        )}

        {state.deliverySource === "Order" && (
          <div style={{ marginTop: 15 }}>
            <StoreOrderSelect
              className={classes.select}
              storeId={state.selectedStoreId}
              onSelected={async order => {
                setState({ ...state, isLoadingOrder: true });
                const result = await StoreOrderApi.getOrder(order.id);
                if (result.type === "OK") {
                  setState({
                    ...state,
                    isLoadingOrder: false,
                    orderToDeliver: {
                      ...result.value,
                      items: result.value.items
                        .filter(i => !i.delivered)
                        .map(i => {
                          i.productId = i.product.id;
                          i.currentlyOnShelf = i.onShelfBeforeDelivery;
                          return i;
                        })
                    }
                  });
                }
              }}
              value={state.orderToDeliver?.id ?? ""}
            />
          </div>
        )}
      </div>

      {state && state.deliverySource === "Order" && state?.isLoadingOrder && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginLeft: 10,
            marginRight: 10,
            marginTop: 25
          }}
        >
          <CircularProgress />
        </div>
      )}

      {state && state.deliverySource === "Order" && state.orderToDeliver && (
        <div style={{ marginLeft: 10, marginRight: 10, marginTop: 25 }}>
          {state.orderToDeliver.items.length > 0 ? (
            <div>
              <h3>{state.steps[2]}. This order contains the following items</h3>
              <div style={{ display: "flex", alignItems: "center" }}>
                <TextField
                  id="search-field"
                  className={classes.searchField}
                  value={state.searchPhrase}
                  placeholder={"Input search phrase"}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position={"end"}>
                        <Clear
                          style={{
                            color: "rgba(0, 0, 0, 0.44)",
                            cursor: "pointer"
                          }}
                          fontSize={"small"}
                          onClick={() =>
                            setState({
                              ...state,
                              searchPhrase: ""
                            })
                          }
                        />
                      </InputAdornment>
                    )
                  }}
                  onChange={e => {
                    setState({ ...state, searchPhrase: e.target.value });
                  }}
                />
                <Button
                  style={{ marginLeft: 10 }}
                  variant={"contained"}
                  color={"primary"}
                  startIcon={<TabletAndroid />}
                  onClick={() =>
                    setState({ ...state, searchShowScanner: true })
                  }
                >
                  Scan
                </Button>
              </div>
              {getOrderItems().map((item: StoreOrderItem) => {
                return (
                  <div
                    style={{
                      marginBottom: 20
                    }}
                  >
                    <div style={{ marginBottom: 30 }}>
                      <Typography>{item.product.name}</Typography>
                      <Typography color="textSecondary">
                        {item.product.gtin}, {item.product.size}{" "}
                        {item.product.brandName}
                      </Typography>
                      <div>
                        <TextField
                          type="number"
                          className={classes.orderItemField}
                          label={t("common.formFields.currentlyOnShelf")}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">st</InputAdornment>
                            )
                          }}
                          value={item?.currentlyOnShelf}
                          helperText={`According to system: ${item.onShelfBeforeDelivery}`}
                          onChange={e => {
                            const value = parseInt(e.target.value);
                            setState({
                              ...state,
                              orderToDeliver: {
                                ...state.orderToDeliver,
                                items: state.orderToDeliver.items.map(
                                  (i: any) => {
                                    if (i.product.id === item.product.id) {
                                      i.currentlyOnShelf = isNaN(value)
                                        ? undefined
                                        : value;

                                      if (value != i.onShelfBeforeDelivery) {
                                        i.discrepancyQuantity = Math.abs(
                                          (isNaN(value) ? 0 : value) -
                                            i.onShelfBeforeDelivery
                                        );
                                      } else {
                                        i.discrepancyQuantity = undefined;
                                      }
                                    }
                                    return i;
                                  }
                                )
                              }
                            });
                          }}
                        />
                        {item?.discrepancyQuantity &&
                          item.discrepancyQuantity > 0 && (
                            <div style={{ marginTop: 10 }}>
                              <Alert severity="warning">
                                {`Discrepancy of ${item.discrepancyQuantity} items detected. Select a reason`}
                              </Alert>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "center"
                                }}
                              >
                                <div style={{ width: "100%" }}>
                                  <SpoilageReasonSelect
                                    className={classes.field}
                                    onChange={event => {
                                      setState({
                                        ...state,
                                        orderToDeliver: {
                                          ...state.orderToDeliver,
                                          items: state.orderToDeliver.items.map(
                                            (i: any) => {
                                              if (
                                                i.product.id == item.product.id
                                              ) {
                                                i.discrepancyReasonId =
                                                  event.target.value;
                                              }
                                              return i;
                                            }
                                          )
                                        }
                                      });
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        <TextField
                          className={classes.orderItemField}
                          label={t("common.formFields.delivering")}
                          type="number"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                {item.unit === OrderUnit.Kfp
                                  ? "ST/KFP"
                                  : `DFP (${
                                      item.distributionPackageSize ?? "..."
                                    } st)`}
                              </InputAdornment>
                            )
                          }}
                          value={item?.quantity}
                          onChange={e => {
                            const value = parseInt(e.target.value);

                            setState({
                              ...state,
                              orderToDeliver: {
                                ...state.orderToDeliver,
                                items: state.orderToDeliver.items.map(
                                  (i: any) => {
                                    if (i.product.id == item.product.id) {
                                      i.quantity = isNaN(value)
                                        ? undefined
                                        : value;
                                    }
                                    return i;
                                  }
                                )
                              }
                            });
                          }}
                        />
                        <h3>{`Total on shelf after delivery: ${calculateOrderItemQuantityAfterDeliver(
                          item
                        )}`}</h3>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            marginTop: 20
                          }}
                        >
                          <Button
                            style={{ width: 200 }}
                            disabled={disabled(item)}
                            variant="contained"
                            color="primary"
                            onClick={async () => {
                              const result = await StoreOrderApi.deliverItem(
                                state.selectedStoreId,
                                state.orderToDeliver.id,
                                item
                              );
                              if (result.type === "OK") {
                                const itemsLeftToDeliver =
                                  state.orderToDeliver.items.filter(
                                    (i: any) => i.id !== item.id
                                  );

                                setState({
                                  ...state,
                                  saveItemSuccess: true,
                                  deliverySource:
                                    itemsLeftToDeliver.length > 0
                                      ? state.deliverySource
                                      : undefined,
                                  orderToDeliver:
                                    itemsLeftToDeliver.length > 0
                                      ? {
                                          ...state.orderToDeliver,
                                          items: itemsLeftToDeliver
                                        }
                                      : undefined
                                });

                                if (itemsLeftToDeliver.length === 0) {
                                  StoreOrderApi.updateOrder(
                                    state.orderToDeliver.id,
                                    StoreOrderState.Delivered
                                  );
                                }
                              } else {
                                setState({
                                  ...state,
                                  saveItemFailed: true,
                                  error: result.details.detail
                                });
                              }
                            }}
                          >
                            {state.orderToDeliver.items.length > 1
                              ? t("common.actions.deliverItem")
                              : t("common.actions.deliverLastItem")}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div>
              <h3>{t("common.messages.allItemsDelivered")}</h3>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: 20
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={async () => {
                    const result = StoreOrderApi.updateOrder(
                      state.orderToDeliver.id,
                      StoreOrderState.Delivered
                    );
                  }}
                >
                  Mark order as delivered
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <Dialog
        fullWidth={true}
        fullScreen={fullScreen}
        maxWidth={"sm"}
        open={state.searchShowScanner}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setState({ ...state, searchShowScanner: false });
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent
          style={
            fullScreen
              ? { padding: 0, marginTop: 15 }
              : { marginTop: 15, marginBottom: 30 }
          }
        >
          <div
            className={classes.scanner}
            style={fullScreen ? { display: "flex", height: "70%" } : {}}
          >
            {state.searchShowScanner && (
              <ScanditBarcodeScanner
                licenseKey={globalSettings.settings.scandit.scannerLicenseKey}
                libraryLocation="https://cdn.jsdelivr.net/npm/@scandit/web-datacapture-barcode@8.0.0/sdc-lib/"
                onScan={(result: any) => {
                  const { barcodes } = result;
                  if (barcodes.length > 0) {
                    const barcodeData = barcodes[0].data;
                    setState({
                      ...state,
                      searchPhrase: barcodeData,
                      searchShowScanner: false
                    });
                  }
                }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <div
        style={{
          display: "flex",
          marginTop: 50,
          justifyContent: "center"
        }}
      >
        <Snackbar
          open={state?.saveItemFailed}
          autoHideDuration={6000}
          onClose={() => {
            setState({
              ...state,
              saveItemFailed: false
            });
          }}
        >
          <Alert severity="error">{state.error}</Alert>
        </Snackbar>
      </div>

      <div
        style={{
          display: "flex",
          marginTop: 50,
          justifyContent: "center"
        }}
      >
        <Snackbar
          open={state?.saveItemSuccess}
          autoHideDuration={6000}
          onClose={() => {
            setState({
              ...state,
              saveItemSuccess: false
            });
          }}
        >
          <Alert severity="success">
            {t("common.messages.itemDeliveredSuccessfully")}
          </Alert>
        </Snackbar>
      </div>

      <Dialog
        fullWidth={true}
        fullScreen={fullScreen}
        maxWidth={"md"}
        open={state.showScanner}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setState({
                ...state,
                showScanner: false,
                scanning: undefined,
                delivering: undefined,
                deliverySuccess: undefined,
                registeringInStore: undefined,
                registeringInSystem: undefined,
                showRegisterProduct: undefined,
                registeringSuccess: undefined,
                registerFailed: false
              });
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        {state.scanning && (
          <DialogContent
            style={
              fullScreen ? { padding: 0, marginTop: 15 } : { marginTop: 15 }
            }
          >
            <div
              className={classes.scanner}
              style={fullScreen ? { display: "flex", height: "70%" } : {}}
            >
              <ScanditBarcodeScanner
                licenseKey={globalSettings.settings.scandit.scannerLicenseKey}
                engineLocation={globalSettings.settings.scandit.scannerEngine}
                guiStyle={globalSettings.settings.scandit.guiStyle}
                paused={false}
                accessCamera={true}
                onReady={() => {}}
                scanSettings={globalSettings.settings.scandit.scanSettings}
                onScan={async (result: ScanResult) => {
                  const { barcodes } = result;
                  if (barcodes.length > 0) {
                    await loadProduct(barcodes[0].data);
                  }
                }}
                onScanError={() => {
                  debugger;
                }}
              />
            </div>
            <TextField
              style={{ width: "100%", marginTop: 10 }}
              type="number"
              label={t("common.formFields.manualInput")}
              value={state.manualInput}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      onClick={async () => {
                        if (state.manualInput) {
                          await loadProduct(state.manualInput);
                        }
                      }}
                    >
                      OK
                    </Button>
                  </InputAdornment>
                )
              }}
              onChange={e => {
                setState({ ...state, manualInput: e.target.value });
              }}
            />
            {state.getItemFailed && (
              <div
                style={{
                  display: "flex",
                  marginTop: 20,
                  justifyContent: "center"
                }}
              >
                <Alert severity="error">{state.error}</Alert>
              </div>
            )}
          </DialogContent>
        )}
        {state.registeringInStore && (
          <DialogContent
            style={
              fullScreen
                ? { padding: 0, marginTop: 15, marginLeft: 15, marginRight: 15 }
                : { marginTop: 15 }
            }
          >
            <div>
              <Alert
                severity="warning"
                style={{ display: "flex", justifyContent: "center" }}
              >
                This product is not registered in{" "}
                <b>{state.selectedStoreName}</b>.
                {currentUser.tenant?.configuration?.features.digitalPriceTags
                  ? ` Click "Connect to price tag" if you have a digital price tag, if not click Activate`
                  : ` Click "Activate" below to register it`}
              </Alert>
              <div
                style={{
                  display: "flex",
                  marginTop: 20,
                  justifyContent: "center"
                }}
              >
                {currentUser.tenant?.configuration?.features
                  .digitalPriceTags && (
                  <>
                    <Button
                      color="primary"
                      startIcon={<Link />}
                      variant="contained"
                      onClick={() =>
                        setState({
                          ...state,
                          showConnectToPriceTag: true,
                          storeId: state.selectedStoreId
                        })
                      }
                    >
                      Connect to price tag
                    </Button>
                    <Typography
                      variant={"body1"}
                      style={{
                        marginLeft: 15,
                        marginRight: 15,
                        alignSelf: "center"
                      }}
                    >
                      OR
                    </Typography>
                  </>
                )}

                <Button
                  style={{ width: 200 }}
                  disabled={state?.registeringProduct === true}
                  variant="contained"
                  color="primary"
                  onClick={async () => {
                    setState({ ...state, registeringProduct: true });
                    const result = await StoreInventoryApi.add(
                      state.selectedStoreId,
                      { gtin: state.manualInput }
                    );
                    if (result.type === "OK") {
                      setState({
                        ...state,
                        registeringInStore: false,
                        registeringProduct: false,
                        registeringSuccess: true
                      });
                    } else {
                      setState({
                        ...state,
                        registerFailed: true,
                        error: result.details.detail
                      });
                    }
                  }}
                >
                  Activate{" "}
                  {state?.registeringProduct === true && (
                    <CircularProgress size={20} style={{ marginLeft: 20 }} />
                  )}
                </Button>
              </div>
              {state.registerFailed && (
                <div
                  style={{
                    display: "flex",
                    marginTop: 20,
                    justifyContent: "center"
                  }}
                >
                  <Alert severity="error">{state.error}</Alert>
                </div>
              )}
            </div>
          </DialogContent>
        )}
        {state.registeringInSystem && (
          <DialogContent
            style={
              fullScreen
                ? { padding: 0, marginTop: 15, marginLeft: 15, marginRight: 15 }
                : { marginTop: 15 }
            }
          >
            <div>
              <Alert
                severity="warning"
                style={{ display: "flex", justifyContent: "center" }}
              >
                This product is not registered in the system
              </Alert>
              <div
                style={{
                  display: "flex",
                  marginTop: 20,
                  justifyContent: "center"
                }}
              >
                <Button
                  style={{ width: 200 }}
                  disabled={state?.registeringProduct === true}
                  variant="contained"
                  color="primary"
                  onClick={async () => {
                    setState({ ...state, registeringProduct: true });
                    const result = await ProductApi.lookup(state.manualInput);

                    if (result.type === "OK") {
                      setState({
                        ...state,
                        productToRegisterInSystem: result.value.product,
                        productImage: result.value.imageData,
                        showRegisterProduct: true,
                        registeringInSystem: false,
                        registeringProduct: false
                      });
                    } else {
                      setState({
                        ...state,
                        productToRegisterInSystem: {
                          ...emptyProduct(),
                          gtin: state.manualInput
                        },
                        showRegisterProduct: true,
                        registeringInSystem: false,
                        registeringProduct: false
                      });
                    }
                  }}
                >
                  Register{" "}
                  {state?.registeringProduct === true && (
                    <CircularProgress size={20} style={{ marginLeft: 20 }} />
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        )}
        {state.showRegisterProduct && (
          <DialogContent
            style={
              fullScreen
                ? { padding: 0, marginTop: 15, marginLeft: 15, marginRight: 15 }
                : { marginTop: 15 }
            }
          >
            <div>
              <TenantProductEditor
                adding={false}
                editingProduct={state.productToRegisterInSystem}
                image={state.productImage}
                onSave={async () => {
                  dispatch(thunkFetchProductsV2());
                  const result = await StoreInventoryApi.add(
                    state.selectedStoreId,
                    { gtin: state.manualInput }
                  );
                  if (result.type === "OK") {
                    setState({
                      ...state,
                      registeringInSystem: false,
                      showRegisterProduct: false,
                      registeringSuccess: true
                    });
                  }
                }}
              />
            </div>
          </DialogContent>
        )}
        {state.registeringSuccess && (
          <DialogContent
            style={
              fullScreen
                ? { padding: 0, marginTop: 15, marginLeft: 15, marginRight: 15 }
                : { marginTop: 15 }
            }
          >
            <div>
              <Alert severity="success">
                The product has been registered in{" "}
                <b>{state.selectedStoreName}</b>. Click the button below to
                start the scanner again
              </Alert>
              <div
                style={{
                  display: "flex",
                  marginTop: 20,
                  justifyContent: "center"
                }}
              >
                <Button
                  style={{ width: 200 }}
                  variant="contained"
                  color="primary"
                  onClick={() =>
                    setState({
                      ...state,
                      registeringSuccess: false,
                      scanning: true,
                      manualInput: undefined
                    })
                  }
                >
                  Start scanning
                </Button>
              </div>
            </div>
          </DialogContent>
        )}

        {state.delivering && (
          <DialogContent
            style={
              fullScreen
                ? { padding: 0, marginTop: 15, marginLeft: 15, marginRight: 15 }
                : { marginTop: 15 }
            }
          >
            <div>
              <Typography>{state.deliveryItem.productName}</Typography>
              <Typography color="textSecondary">
                {state.deliveryItem.gtin}, {state.deliveryItem.size}{" "}
                {state.deliveryItem.brandName}
              </Typography>
            </div>
            <TextField
              className={classes.field}
              label={t("common.formFields.currentlyOnShelf")}
              type="number"
              value={state?.deliveryItem?.correctedOnShelf}
              helperText={`According to system: ${state.deliveryItem.onShelfBeforeDelivery}`}
              onChange={e => {
                const value = parseInt(e.target.value);

                const {
                  deliveryItem: { onShelfBeforeDelivery }
                } = state;

                let discrepancyQuantity = undefined;

                if (value != onShelfBeforeDelivery) {
                  discrepancyQuantity = Math.abs(
                    (isNaN(value) ? 0 : value) - onShelfBeforeDelivery
                  );
                }

                setState({
                  ...state,
                  discrepancyItem:
                    discrepancyQuantity === undefined
                      ? undefined
                      : {
                          ...state.discrepancyItem,
                          quantity: discrepancyQuantity
                        },
                  deliveryItem: {
                    ...state.deliveryItem,
                    correctedOnShelf: isNaN(value) ? undefined : value
                  }
                });
              }}
            />
            {state.discrepancyItem &&
              state.discrepancyItem.quantity &&
              state.discrepancyItem.quantity > 0 && (
                <div style={{ marginTop: 10 }}>
                  <Alert severity="warning">
                    {`Discrepancy of ${state.discrepancyItem.quantity} items detected. Select a reason and click Save`}
                  </Alert>
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <div style={{ width: "100%" }}>
                      <SpoilageReasonSelect
                        className={classes.field}
                        onChange={event => {
                          setState({
                            ...state,
                            discrepancyItem: {
                              ...state.discrepancyItem,
                              reasonId: event.target.value
                            }
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            <TextField
              className={classes.field}
              type="number"
              label={t("common.formFields.deliveredToShelf")}
              value={state?.deliveryItem?.quantityDelivered}
              onChange={e => {
                const value = parseInt(e.target.value);
                setState({
                  ...state,
                  deliveryItem: {
                    ...state.deliveryItem,
                    quantityDelivered: isNaN(value) ? undefined : value
                  }
                });
              }}
            />
            {state.deliveryItem &&
              state?.deliveryItem?.quantityDelivered > 0 && (
                <h3>{`Total on shelf after delivery: ${totalQuantityDelivered()}`}</h3>
              )}

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 20
              }}
            >
              <Button
                style={{ width: 200 }}
                variant="contained"
                color="primary"
                disabled={
                  state.deliveryItem.quantityDelivered == undefined ||
                  (state?.deliveryItem?.quantityDelivered === 0 &&
                    state?.discrepancyItem?.quantity > 0 &&
                    !state.discrepancyItem.reasonId)
                }
                onClick={async () => {
                  const {
                    deliveryItem: {
                      productId,
                      onShelfBeforeDelivery,
                      correctedOnShelf,
                      quantityDelivered
                    }
                  } = state;

                  const { discrepancyItem } = state || {};
                  const { quantity, reasonId } = discrepancyItem || {};

                  const result = await ProductApi.reportDelivery({
                    source: { type: "distributor" },
                    destination: {
                      type: "store",
                      id: state.selectedStoreId
                    },
                    item: {
                      productId: productId,
                      onShelfQuantityBeforeDelivery:
                        correctedOnShelf ?? onShelfBeforeDelivery,
                      onShelfQuantityDelivered: quantityDelivered
                    },
                    spoilageReport:
                      quantity && reasonId
                        ? { quantity, comment: "", reasonId: reasonId }
                        : undefined
                  });
                  if (result.type === "OK") {
                    setState({
                      ...state,
                      deliverySuccess: true,
                      discrepancyItem: undefined,
                      delivering: false
                    });
                  } else {
                    setState({
                      ...state,
                      deliveryFailed: true,
                      error: result.details.detail
                    });
                  }
                }}
              >
                Save
              </Button>
            </div>
            {state.deliveryFailed && (
              <div
                style={{
                  display: "flex",
                  marginTop: 20,
                  justifyContent: "center"
                }}
              >
                <Alert severity="error">{state.error}</Alert>
              </div>
            )}
          </DialogContent>
        )}
        {state.deliverySuccess && (
          <DialogContent>
            <div>
              <div
                style={{
                  display: "flex",
                  marginTop: 20,
                  justifyContent: "center"
                }}
              >
                <Alert severity="success">
                  {t("common.messages.itemDelivered")}
                </Alert>
              </div>
              <div
                style={{
                  display: "flex",
                  marginTop: 20,
                  justifyContent: "center"
                }}
              >
                <Button
                  style={{
                    width: 200
                  }}
                  variant="contained"
                  color="primary"
                  onClick={async () => {
                    setState({
                      ...state,
                      scanning: true,
                      delivering: false,
                      deliverySuccess: false,
                      item: undefined,
                      manualInput: undefined
                    });
                  }}
                >
                  Scan more items
                </Button>
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>

      <Dialog
        fullWidth
        fullScreen={fullScreen}
        maxWidth="md"
        open={state.showConnectToPriceTag}
        PaperProps={{
          style: fullScreen
            ? { height: "100dvh", display: "flex", flexDirection: "column" }
            : { height: "80vh", display: "flex", flexDirection: "column" } // desktop height
        }}
      >
        <MuiDialogTitle disableTypography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => setState({ ...state, showConnectToPriceTag: false })}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>

        <DialogContent
          style={{
            flex: 1, // IMPORTANT
            minHeight: 0, // IMPORTANT (allows children to shrink properly)
            padding: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column"
          }}
        >
          <ConnectToPriceTag
            addToStore
            storeId={state.selectedStoreId}
            gtin={state.manualInput}
            onSuccess={() =>
              setState({
                ...state,
                showConnectToPriceTag: false,
                registeringSuccess: true,
                registeringInStore: false
              })
            }
            onCancel={() =>
              setState({
                ...state,
                showConnectToPriceTag: false,
                storeId: ""
              })
            }
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};
